import { ReactNode, useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Loader2, Play, Undo2 } from 'lucide-react';

import { Button } from '@sandtech-hub-monorepo/ui-kit';
import {
  GetViewerAvailableProgramsDocument,
  ViewerDocument,
} from '@sandtech-hub-monorepo/ehub-client';
import { Loading } from '@sandtech-hub-monorepo/ui-kit-site';
import {
  trackEnrollmentStarted,
  trackProgramDetailView,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import {
  CatalogueProgram,
  programDescription,
  programImage,
  programSectionSummary,
  programSectionTitle,
  programTitle,
} from '../../data/programs';
import { useEnrollInProgram } from '../../data/useEnrollInProgram';
import { useProgramCoverage } from '../../data/useProgramCoverage';
import { AppRoutesPaths, subscribePath } from '../../routes/types';
import { DetailStatsStrip } from './components/DetailStatsStrip';
import { ProgramStructure } from './components/ProgramStructure';
import { ValueChecklist } from './components/ValueChecklist';
import { ProgramCredentials } from './components/ProgramCredentials';

// Generic freemium value props — the same across programs today; these move to
// the backend with the rest of the program schema (tracked as debt).
const INCLUDED_WITH_FREE = [
  'An applied AI use case',
  'Guided exercises & demos',
  'AI foundations & machine intelligence basics',
];
const UNLOCKED_WITH_SUBSCRIPTION = [
  'A working prototype',
  'Built around real problems in your field',
  'Career-ready certificate of completion',
];
const MACRO_POTENTIAL = [
  'Sharable online badges',
  'Unlock the full program, projects, and earn a Certificate',
];

export const GsaProgramDetail = () => {
  // `:programId` carries the abbreviation — see AppRoutesPaths.Program for
  // why the param cannot be renamed.
  const { programId: abbreviation } = useParams();
  const navigate = useNavigate();
  const { data } = useQuery(ViewerDocument, { fetchPolicy: 'cache-first' });

  // Resolved out of the same list the catalogue renders, so a program that
  // lists is a program that opens — and, being cache-first, arriving from the
  // catalogue costs no request.
  const { data: programsData, loading } = useQuery(
    GetViewerAvailableProgramsDocument,
    { fetchPolicy: 'cache-first' }
  );

  /*
   * Degree programs are excluded, not just unmatched: `availablePrograms`
   * returns both product types, so without this a degree abbreviation resolves
   * here and renders in the GSA shell — which carries a Get Started button
   * that enrols. A GSA learner asking for a degree program gets the 404, same
   * as the degree page does in reverse.
   */
  const program = useMemo<CatalogueProgram | undefined>(() => {
    const key = (abbreviation ?? '').toLowerCase();
    return (programsData?.viewer?.availablePrograms ?? []).find(
      (candidate) =>
        candidate.abbreviation.toLowerCase() === key &&
        !candidate.isUniversityProgram
    );
  }, [programsData, abbreviation]);

  // Entitlement is a join: a subscription unlocks every program its plan
  // lists, so this asks about THIS program rather than reading a flag off the
  // learner. Called before the guards below, as hooks must be.
  //
  // `loading` matters here because coverage decides where the CTA goes, not
  // just how it looks: until the answer arrives `covered` is false, and a
  // learner who clicked in that window would be sent to buy a plan they
  // already hold.
  const { covered, loading: coverageLoading } = useProgramCoverage(
    program?.id ?? null
  );
  const { enroll, enrolling } = useEnrollInProgram();

  // Once per program, not on every refetch of the list it comes from.
  useEffect(() => {
    if (program) trackProgramDetailView(program);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id]);

  if (loading) {
    return <Loading />;
  }

  // No program, or one from the degree half of the catalogue: both are "no
  // such program for you", and both land on the 404 rather than an in-page
  // message, matching what the degree detail does.
  if (!program) {
    return <Navigate to={AppRoutesPaths.NotFound} replace />;
  }

  const goToCatalogue = () => navigate(AppRoutesPaths.Programs);
  // Plans are scoped to a program — `billingPlansForProgram` takes one, and
  // checkout refuses a plan that does not cover it — so the id travels with
  // the CTA rather than leaving the plans page to guess.
  const onSubscribe = () => navigate(subscribePath(program.id));

  /*
   * Enrolled or graduated: Savanna scopes a flow to a learner it knows, and
   * eHub tells it about this one on enrolment (savannaAssignLearnerToProgram)
   * - that assignment outlives graduation, so a graduated learner still has a
   * real Savanna flow to read. Before either, the curriculum is read with no
   * learner attached.
   */
  const curriculumSandId =
    program.isEnrolled || program.isGraduated
      ? data?.viewer?.sandId ?? null
      : null;

  // Read once: it decides whether the section renders and is also its body.
  const sectionSummary = programSectionSummary(program);

  const enrollButtonClass =
    'w-full rounded-full bg-saffron text-foreground hover:bg-saffron-active';

  let cta: ReactNode;

  if (program.isEnrolled) {
    /*
     * Enrolled splits on coverage. Covered, there is nothing left to sell and
     * the CTA just points at the content. Uncovered, the structure below is
     * already the upsell - every paid activity carries a lock - so the CTA
     * names the thing that would remove them.
     *
     * The uncovered half is meant to be unreachable: enrolling goes through
     * the plan picker, so a learner arrives here having chosen free or paid.
     * It is rendered rather than assumed away because "free" is a choice they
     * can make, and because an expiring subscription lands them here too.
     */
    cta = coverageLoading ? (
      // Neither branch below is safe to assert yet: `covered` defaults to
      // false while the read is in flight, so rendering on it here would
      // flash the "Subscribe" upsell at a learner who turns out to be
      // covered the moment the answer lands.
      <Button disabled className={enrollButtonClass}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    ) : covered ? (
      <Button
        onClick={() => navigate(AppRoutesPaths.Learning)}
        className={enrollButtonClass}
      >
        Continue Learning
      </Button>
    ) : (
      <>
        <Button
          onClick={onSubscribe}
          disabled={coverageLoading}
          className={enrollButtonClass}
        >
          Subscribe to All Access
        </Button>
        <p className="text-sm text-muted-foreground">
          You&apos;re enrolled and your free modules are open. A subscription
          unlocks the rest of this program.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate(AppRoutesPaths.Learning)}
          className="w-full rounded-full"
        >
          Continue Learning
        </Button>
      </>
    );
  } else if (program.isGraduated) {
    cta = (
      <Button disabled className={enrollButtonClass}>
        Completed
      </Button>
    );
  } else if (program.canEnroll) {
    cta = (
      <>
        <div className="rounded-xl bg-background-quaternary p-4 text-sm text-foreground">
          By clicking &quot;Get Started&quot;, you confirm that you&apos;re 18
          or older and agree to our{' '}
          <Link
            to="https://www.frontiertech.edu/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            Terms
          </Link>{' '}
          and{' '}
          <Link
            to="https://frontiertech.edu/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            Privacy Policy
          </Link>
          .
        </div>

        {covered && (
          <p className="text-sm font-medium text-success">
            Your subscription already covers this program — no payment needed.
          </p>
        )}
        <Button
          onClick={() => {
            trackEnrollmentStarted(program);
            if (covered) enroll(program.id);
            else onSubscribe();
          }}
          disabled={coverageLoading || enrolling}
          className={enrollButtonClass}
        >
          {enrolling ? 'Enrolling…' : 'Get Started'}
        </Button>
      </>
    );
  } else {
    cta = (
      <>
        <Button disabled className={enrollButtonClass}>
          Get Started
        </Button>
        <p className="text-sm text-muted-foreground">
          You can&apos;t start this program right now. If you are already in
          another program, finishing or leaving it may free this one up.
        </p>
      </>
    );
  }

  return (
    <div className="bg-background pb-16">
      {/* Hero + stats */}
      <div className="bg-heading">
        <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-8 md:px-12 py-10 text-white">
          <h1 className="text-3xl font-semibold text-white md:text-4xl">
            {programTitle(program)}
          </h1>
        </div>
      </div>
      {/* Hides itself when marketing has written none of its copy. */}
      <div className="bg-primary">
        <DetailStatsStrip
          program={program}
          className="mx-auto w-full max-w-screen-2xl px-4 sm:px-8 md:px-12 py-10"
        />
      </div>

      {/* Body */}
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-8 px-4 sm:px-8 md:px-12 py-10 lg:flex-row">
        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col gap-10">
          {/*
            Marketing's own explanation of the program, above the structure:
            what it is, before what is in it. Previously fed by a mock field
            that no backend populated, so this section never appeared.
          */}
          {sectionSummary && (
            <section>
              <h2 className="text-2xl font-semibold text-heading">
                {programSectionTitle(program)}
              </h2>
              {/*
                `whitespace-pre-line` because the copy is authored with real
                paragraph breaks and arrives as one string.
              */}
              <p className="mt-4 whitespace-pre-line text-muted-foreground">
                {sectionSummary}
              </p>
            </section>
          )}

          <ProgramStructure
            programId={program.id}
            sandId={curriculumSandId}
            covered={covered}
            onSubscribe={onSubscribe}
            description={programDescription(program)}
          />
        </div>

        {/*
          Sidebar. `lg:-mt-56` pulls it up to overlap the hero + stats bar,
          matching Figma's Left Card (absolute y=163) starting well above the
          main content column (y=386, right where the hero+stats bar ends at
          y=354) — a ~223px overlap. Only from `lg:` up, where it's already a
          separate column next to the main content.
        */}
        <div className="flex w-full shrink-0 flex-col gap-6 lg:-mt-56 lg:w-[420px]">
          {/*
            Mobile keeps the thumbnail as its own separate rounded box with a
            gap before the content card below, matching Figma's mobile frame.
            From `sm:` up they merge into one continuous card — only the top
            corners round on the image, only the bottom corners round on the
            content underneath, sharing one border — matching the desktop
            frame instead.
          */}
          <div className="flex flex-col gap-6 sm:gap-0 sm:overflow-hidden sm:rounded-lg sm:bg-card">
            <div className="relative aspect-video overflow-hidden rounded-lg bg-heading sm:rounded-none">
              <img
                src={programImage(program)}
                alt={programTitle(program)}
                className="absolute inset-0 h-full w-full object-cover"
              />
              {/* Nothing to play without a marketing video — the thumbnail
                  is just a thumbnail then, not an implied video player. */}
              {program.marketing.video && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-heading">
                    <Play className="h-6 w-6 fill-current" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-6 rounded-b-lg bg-card p-6 sm:rounded-none">
              <ValueChecklist
                title="Included with Free"
                items={INCLUDED_WITH_FREE}
              />
              <ValueChecklist
                title="Unlocked with Subscription"
                items={UNLOCKED_WITH_SUBSCRIPTION}
                variant="saffron"
                className="rounded-xl bg-background p-4"
              />
              {cta}
              <button
                type="button"
                onClick={goToCatalogue}
                className="flex items-center justify-center gap-2 font-medium text-primary"
              >
                {/* Always the GSA catalogue: a degree program never reaches
                    this page, it is turned away to the 404 above. */}
                Back to Global Skills Academy
                <Undo2 className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-5 rounded-2xl border border-border-secondary bg-card p-6">
            <ValueChecklist title="Macro Potential" items={MACRO_POTENTIAL} />
            <ProgramCredentials program={program} covered={covered} />
          </div>
        </div>
      </div>
    </div>
  );
};
